/**
 * LOS TEXTOS DE «QUÉ INCLUYE» DE GTR GO, GTR FLY Y GTR WIN.
 * =============================================================================
 *
 * Antes cada card llevaba su lista de beneficios escrita a mano dentro de su
 * propio `<template>`. Eran cuatro listas —`GtrGo.vue`, `GtrFly.vue`,
 * `GtrWin.vue` y `GtrBasic.vue`, que también vende Fly— y nada obligaba a que
 * dijeran lo mismo. De hecho no lo decían: eran copias literales de las cards
 * viejas (Lite / Academia / VIP) con el marketing del catálogo retirado.
 *
 * LA REGLA QUE ESTE FICHERO EXISTE PARA NO PERDER: LOS PAQUETES SON ACUMULATIVOS
 * -----------------------------------------------------------------------------
 * FLY incluye TODO lo de GO, y además lo suyo. WIN incluye TODO lo de GO y de
 * FLY, y además lo suyo. Eso NO se deja a que el cliente lo deduzca comparando
 * tres tarjetas: cada card lo dice con todas las letras en una banda bajo el
 * título («INCLUYE TODO LO DE GO», «INCLUYE TODO LO DE GO Y FLY»), igual que la
 * lámina comercial.
 *
 * Y como es una regla y no un texto, la banda NO se escribe: se CALCULA a partir
 * del orden de `CADENA_DE_PAQUETES`. Si mañana se mete un paquete en medio, la
 * banda de los de arriba se corrige sola. Un texto escrito a mano en tres
 * plantillas es exactamente la clase de cosa que se queda vieja en una sola.
 *
 * DOS BLOQUES POR TARJETA, Y POR QUÉ NO PUEDE SER UNO SOLO
 * -----------------------------------------------------------------------------
 * La lámina comercial trae ONCE beneficios numerados del 1 al 11. Pero la
 * aplicación entrega más cosas que las que caben en una lámina: hay beneficios
 * que llevan años en las cards y que se siguen dando (la plataforma, el plan de
 * compensación, la beca, los grupos de apoyo…).
 *
 * Meterlos todos en la misma lista numerada convertiría el 1-11 en un 1-22, y a
 * partir de ahí la lámina que se usa en las presentaciones y la pantalla que ve
 * el cliente dejarían de decir el mismo número para la misma cosa. El «nº 7» es
 * una referencia que la gente usa en voz alta; no puede significar dos cosas.
 *
 * Así que cada paquete tiene DOS bloques:
 *
 *   1. `sections` — LOS ONCE DE LA LÁMINA, numerados, y numerados de corrido a
 *      través de los tres paquetes. Es el bloque que hay que poder señalar con
 *      el dedo en la lámina y encontrar igual en la pantalla.
 *   2. `alsoIncludes` — «ADEMÁS INCLUYE», SIN NUMERAR. Todo lo demás que el
 *      paquete entrega. No numera a propósito: en cuanto llevara números,
 *      alguien los sumaría a los de arriba y estaríamos donde no queremos.
 *
 * `numerarCadena` sólo recorre el bloque 1. El 2 lo copia tal cual.
 *
 * LA NUMERACIÓN 1..11 ES UNA SOLA SECUENCIA, NO TRES
 * -----------------------------------------------------------------------------
 * En la lámina los beneficios van numerados del 1 al 11 SEGUIDOS, atravesando
 * los tres paquetes: GO se lleva del 1 al 5, FLY del 6 al 9 y WIN el 10 y el 11.
 * No reinicia en cada tarjeta, y eso es parte del mensaje: el número dice en qué
 * punto de la escalera estás, no cuántas cosas trae tu paquete.
 *
 * Por eso los números NO están escritos en la declaración de abajo. Se asignan
 * recorriendo la cadena en orden (`numerarCadena`), así que añadir un beneficio
 * a GO renumera FLY y WIN sin que nadie tenga que acordarse. Escribir el «6.» a
 * mano en la card de FLY habría funcionado hasta el primer cambio en GO.
 *
 * ⚠️ CUATRO BENEFICIOS VIEJOS ESTÁN AQUÍ CON OTRO NOMBRE — NO LOS VUELVAS A AÑADIR
 * -----------------------------------------------------------------------------
 * Esto es lo que hace que el catálogo nuevo PAREZCA mucho más corto que el
 * viejo sin serlo. Son el mismo producto rebautizado, y los cuatro ya están en
 * el bloque numerado. Quien compare esta lista con una card vieja va a echar de
 * menos el nombre antiguo y va a querer reponerlo: lo estaría duplicando.
 *
 *   · «GTR Cryptospace»   ES  Criptomonedas      (nº 1)
 *   · «GTR Market-king»   ES  Marketing Digital  (nº 2)
 *   · «GTR Social Impact» ES  Redes Sociales     (nº 3)
 *   · «GTR English»       ES  Inglés Comercial   (nº 4)
 *
 * Dos de ellos además CAMBIAN DE PAQUETE, y en la misma dirección: «GTR
 * Market-king» y «GTR English» estaban en FLY y sus equivalentes están en GO.
 * Bajan un escalón, o sea que pasa a tenerlos todo el mundo. No es una errata de
 * la lámina.
 *
 * CUATRO QUE SE VAN, Y NO VUELVEN
 * -----------------------------------------------------------------------------
 * «GTR Ai Money», «GTR Travel» y «GTR Points» estaban en la card de Fly y salen
 * del catálogo comercial.
 *
 * «Canales de Alertas» estaba en GO y también sale, pero por otro motivo: no se
 * retira, es que ya estaba dicho dos veces. El nº 5 lo nombra dentro de su
 * paréntesis («canal de señales»), así que la línea suelta sólo repetía.
 *
 * Ninguno de los cuatro está abajo, y no es un olvido.
 *
 * DÓNDE VIVE ESTO Y DÓNDE NO
 * -----------------------------------------------------------------------------
 * Vive en el FRONT, no en Firestore. El documento de catálogo
 * `subscriptions/{gtr-go,gtr-fly,gtr-win}` no tiene ningún campo de beneficios:
 * su interfaz (`functions/src/Subscriptions/SubscriptionsInterfaces.ts` :30-71)
 * sólo lleva `displayName`, `renewDisplayName` y `commissionRecordConcept` como
 * texto de cara al cliente. El precio, la duración y el nombre siguen saliendo
 * del catálogo; lo de aquí es sólo la copy comercial.
 */

/** El encabezado del segundo bloque. Se escribe una vez y lo pintan las cuatro. */
export const TITULO_ADEMAS_INCLUYE = "Además incluye";

/** Un beneficio del bloque SIN numerar. */
export interface BenefitExtra {
  text: string;
  /** El matiz que en la card va entre paréntesis y más pequeño. */
  note?: string;
}

/** Un beneficio de la lámina, ya con su número dentro de la secuencia continua. */
export interface BenefitItem extends BenefitExtra {
  /** Posición en la secuencia 1..11 que atraviesa los TRES paquetes. */
  n: number;
}

/** El bloque numerado, con su encabezado («MONETIZACIÓN FLEXIBLE», …). */
export interface BenefitSection {
  title: string;
  items: BenefitItem[];
}

/** El bloque «Además incluye»: mismo formato, pero sus ítems no llevan número. */
export interface ExtraSection {
  title: string;
  items: BenefitExtra[];
}

export interface PackageBenefits {
  /** El id del documento de catálogo, para poder cruzarlo si hace falta. */
  id: string;
  /** Cómo se le nombra dentro de la banda de acumulación: «GO», «FLY», «WIN». */
  label: string;
  /** El renglón bajo el título: «Membresía Base», «Membresía Intermedia», … */
  subtitle: string;
  /**
   * La banda: «INCLUYE TODO LO DE GO». `null` en el primer paquete de la
   * cadena, que no acumula nada porque es la base.
   */
  includesBanner: string | null;
  /** BLOQUE 1 — los de la lámina, numerados de corrido. */
  sections: BenefitSection[];
  /** BLOQUE 2 — «Además incluye», sin numerar. `null` si el paquete no tiene. */
  alsoIncludes: ExtraSection | null;
}

/** Lo mismo, pero tal y como se DECLARA: sin números y sin banda. */
interface PaqueteDeclarado {
  id: string;
  label: string;
  subtitle: string;
  sections: { title: string; items: BenefitExtra[] }[];
  alsoIncludes: BenefitExtra[];
}

/**
 * LA CADENA, EN ORDEN DE MENOR A MAYOR. El orden es el dato: de él salen la
 * numeración continua y el texto de la banda de acumulación.
 */
const CADENA_DE_PAQUETES: PaqueteDeclarado[] = [
  {
    id: "gtr-go",
    label: "GO",
    subtitle: "Membresía Base",
    sections: [
      {
        title: "Monetización flexible",
        items: [
          // Antes se llamaba «GTR Cryptospace».
          { text: "Criptomonedas", note: "Cursos grabados" },
          // Antes se llamaba «GTR Market-king» y estaba en FLY: baja a GO.
          { text: "Marketing Digital", note: "Cursos grabados" },
          // Antes se llamaba «GTR Social Impact».
          { text: "Redes Sociales", note: "Cursos grabados" },
          // Antes se llamaba «GTR English» y estaba en FLY: baja a GO.
          { text: "Inglés Comercial", note: "Cursos grabados" },
        ],
      },
      {
        title: "Deportes",
        items: [
          {
            // El «canal de señales» de este paréntesis es el que antes iba
            // suelto en la card de GO como «Canales de Alertas». Está dicho
            // aquí, así que la línea suelta se retiró; no la repongas.
            text: "GTR Sports — Alertas Estándar",
            note: "Clases en vivo + grabadas + canal de señales",
          },
        ],
      },
    ],
    alsoIncludes: [
      { text: "Acceso a la Plataforma" },
      {
        text: "Comisiones por Ventas Directas",
        // El matiz venía en la card vieja como nota al pie y se conserva: sin
        // licencia no se cobra, y prometer la comisión sin decirlo es una venta
        // que acaba en una reclamación.
        note: "Requiere Licencia de Distribuidor",
      },
      // TELEGRAM, no WhatsApp. Los grupos de apoyo viven en Telegram —es donde
      // están de verdad, y donde ya viven los canales de alertas de la propia
      // plataforma—; el texto de WhatsApp venía heredado de la card vieja
      // (`GtrLite.vue`) y nombraba una aplicación en la que no hay nada.
      //
      // La app se NOMBRA a propósito, en vez de dejarlo en «Grupos de Apoyo» a
      // secas como hace la tarjeta de WIN: quien compra GO tiene que saber a
      // dónde va a tener que entrar, y aquí es el único sitio donde se le dice.
      // (Hector, 2026-07-28: ya corregido también en la página pública.)
      { text: "Apoyo en Grupo de Telegram" },
    ],
  },
  {
    id: "gtr-fly",
    label: "FLY",
    subtitle: "Membresía Intermedia",
    sections: [
      {
        title: "Deportes",
        items: [{ text: "GTR Sports — Alertas Premium" }],
      },
      {
        title: "Copia de expertos",
        items: [
          { text: "Forex", note: "Señales continuas" },
          { text: "Índices Sintéticos", note: "Señales continuas" },
          { text: "Opciones Binarias", note: "Alertas EN VIVO" },
        ],
      },
    ],
    alsoIncludes: [
      { text: "Plan de Compensación GTR" },
      { text: "Acceso a Beca GTR" },
    ],
  },
  {
    id: "gtr-win",
    label: "WIN",
    subtitle: "Membresía Élite Total",
    sections: [
      {
        title: "Exclusivos paquete Win",
        items: [
          { text: "GTR Digital Lab" },
          // 🔴 «ACELERADOR DE RIQUEZA», NUNCA «CMMI», «CMI» NI «COLOMBIA
          // MASTERMIND INTENSIVE». Aquí decía lo último y era el nombre
          // RETIRADO (decisión de Hector, 2026-07-28).
          //
          // Y no es un cambio de etiqueta comercial: el producto dejó de ser
          // «el mastermind de Colombia» porque hoy lleva dentro mentorías que
          // no son de aquel evento. El nombre viejo describe algo que ya no es.
          //
          // `Acelerador de Riqueza` es además el `displayName` del documento de
          // catálogo `subscriptions/mmi-colombia`, así que la tarjeta y el
          // catálogo dicen lo mismo. El id del documento sigue siendo
          // `mmi-colombia` y no se toca — es la llave, no el nombre.
          { text: "Acelerador de Riqueza" },
        ],
      },
    ],
    alsoIncludes: [
      { text: "Plan de Acción del primer mes" },
      { text: "Mentorías de líderes 6 cifras" },
      { text: "Desarrollo de tu Marca Personal" },
      { text: "Acceso al Bono Go Live!" },
      { text: "Grupos de Apoyo" },
      // 🔴 EL 100% VA CON SU ORIGEN PEGADO, Y NO ES ESTILO: SIN ÉL ES MENTIRA.
      //
      // La card vieja (`GtrVip.vue`) decía «Acceso a cobrar el 100% de
      // comisiones (CMI)», y una versión intermedia de este fichero lo dejó en
      // «Acceso a cobrar el 100% de comisiones», a secas, razonando que nombrar
      // el Acelerador se leería como que hay que comprarlo aparte.
      //
      // Ese razonamiento cambió un problema por otro peor (Hector, 2026-07-28):
      // sin decir de qué es el 100%, la frase promete el 100% de TODAS las
      // comisiones, que es falso. El 100% es del ACELERADOR y de nada más.
      // Prometer de más en una tarjeta de compra no acaba en una queja de
      // estilo, acaba en una reclamación de dinero.
      //
      // El miedo de la versión anterior se resuelve con el encabezado de la
      // tarjeta, no callando: esto está bajo «Además incluye» de WIN, o sea que
      // ya viene dentro. Nada aquí sugiere una compra aparte.
      { text: "Acceso a cobrar el 100% del Acelerador" },
    ],
  },
];

/**
 * Recorre la cadena en orden y devuelve cada paquete ya numerado y con su banda.
 *
 * El contador NO se reinicia entre paquetes: ése es justamente el punto. Y sólo
 * cuenta el bloque de la lámina (`sections`); `alsoIncludes` pasa de largo, que
 * es lo que mantiene el 1..11 pegado a la lámina en vez de convertirlo en 1..22.
 *
 * La banda se arma con los `label` de los paquetes ANTERIORES, unidos con « Y »
 * («GO», «GO Y FLY»), que es como está redactada en la lámina.
 */
function numerarCadena(cadena: PaqueteDeclarado[]): PackageBenefits[] {
  let contador = 0;
  return cadena.map((paquete, indice) => {
    const anteriores = cadena.slice(0, indice).map((p) => p.label);
    return {
      id: paquete.id,
      label: paquete.label,
      subtitle: paquete.subtitle,
      includesBanner:
        anteriores.length > 0
          ? `Incluye todo lo de ${anteriores.join(" y ")}`
          : null,
      sections: paquete.sections.map((seccion) => ({
        title: seccion.title,
        items: seccion.items.map((item) => ({
          n: ++contador,
          text: item.text,
          note: item.note,
        })),
      })),
      alsoIncludes:
        paquete.alsoIncludes.length > 0
          ? { title: TITULO_ADEMAS_INCLUYE, items: paquete.alsoIncludes }
          : null,
    };
  });
}

const PAQUETES_NUMERADOS = numerarCadena(CADENA_DE_PAQUETES);

export const BENEFICIOS_GO: PackageBenefits = PAQUETES_NUMERADOS[0];
export const BENEFICIOS_FLY: PackageBenefits = PAQUETES_NUMERADOS[1];
export const BENEFICIOS_WIN: PackageBenefits = PAQUETES_NUMERADOS[2];

/** Por id de catálogo, para quien tenga el `subscriptionId` y no la constante. */
export const BENEFICIOS_POR_PAQUETE: Record<string, PackageBenefits> =
  PAQUETES_NUMERADOS.reduce((acc, paquete) => {
    acc[paquete.id] = paquete;
    return acc;
  }, {} as Record<string, PackageBenefits>);
